import { supabase } from '../supabase';
import type { ServiceCostTemplate, ServiceCostTemplateFormData } from '../types';

export class ServiceCostTemplateService {
  /**
   * Get all cost templates for a specific service
   */
  static async getByServiceId(serviceId: string): Promise<ServiceCostTemplate[]> {
    try {
      const { data, error } = await supabase
        .from('service_cost_templates')
        .select(`
          *,
          cost_category:service_cost_categories(*)
        `)
        .eq('service_id', serviceId)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching service cost templates:', error);
      throw error;
    }
  }

  /**
   * Create a new cost template for a service
   */
  static async create(serviceId: string, data: ServiceCostTemplateFormData): Promise<ServiceCostTemplate> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      const templateData = {
        ...data,
        service_id: serviceId,
        organization_id: profile.organization_id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: template, error } = await supabase
        .from('service_cost_templates')
        .insert(templateData)
        .select(`
          *,
          cost_category:service_cost_categories(*)
        `)
        .single();

      if (error) throw error;

      return template;
    } catch (error) {
      console.error('Error creating service cost template:', error);
      throw error;
    }
  }

  /**
   * Update an existing cost template
   */
  static async update(id: string, data: Partial<ServiceCostTemplateFormData>): Promise<ServiceCostTemplate> {
    try {
      const updateData = {
        ...data,
        updated_at: new Date().toISOString()
      };

      const { data: template, error } = await supabase
        .from('service_cost_templates')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          cost_category:service_cost_categories(*)
        `)
        .single();

      if (error) throw error;

      return template;
    } catch (error) {
      console.error('Error updating service cost template:', error);
      throw error;
    }
  }

  /**
   * Delete a cost template
   */
  static async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('service_cost_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting service cost template:', error);
      throw error;
    }
  }

  /**
   * Get cost categories for dropdowns
   */
  static async getCostCategories(): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('service_cost_categories')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching cost categories:', error);
      throw error;
    }
  }
}